import type { CrmDealDto } from '@upme/contracts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@upme/ui/components/tooltip';
import { cn } from '@upme/ui/lib/utils';
import type { CrmCardRole, CrmCardRoleKind, CrmRoleActivityStatus } from '@upme/view-logic';
import { CRM_CARD_ROLE_LABEL_RU, crmRoleActivityTooltip } from '@upme/view-logic';

const ROLE_TAG_CLASS: Record<CrmCardRoleKind, string> = {
  organizer: 'border-cyan-500/45 text-cyan-800 dark:text-cyan-200',
  participant: 'border-border/80 bg-transparent text-muted-foreground',
  specialist: 'border-violet-500/45 text-violet-800 dark:text-violet-200',
  client: 'border-orange-500/45 text-orange-800 dark:text-orange-200',
};

const STATUS_RING_CLASS: Record<CrmRoleActivityStatus, string> = {
  outside: 'border-muted-foreground/50',
  guest: 'border-amber-500/80',
  active: 'border-emerald-500/80',
};

function RoleStatusRing({ status }: { status: CrmRoleActivityStatus }) {
  return (
    <span
      className={cn(
        'inline-block size-3 shrink-0 rounded-full border bg-transparent',
        STATUS_RING_CLASS[status],
      )}
      aria-hidden
    />
  );
}

type Props = {
  role: CrmCardRole;
  contact: CrmDealDto['contact'];
};

export function CrmRoleTag({ role, contact }: Props) {
  const tooltip = crmRoleActivityTooltip(role.kind, role.status, contact);
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex h-[25px] items-center gap-1.5 rounded-sm border px-2 text-sm leading-none',
              ROLE_TAG_CLASS[role.kind],
            )}
          >
            <RoleStatusRing status={role.status} />
            {CRM_CARD_ROLE_LABEL_RU[role.kind]}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
